import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDAlert,
  FDBox,
  FDDeleteButton,
  FDHeadlineText,
  FDPaper,
  FDPrimaryActionButton,
  FDStack, FDTextButton,
  FDTitleText,
} from '@facet-wealth/design';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import MetaFormFields from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/MetaFormFields';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PageModalTitle from 'Views/Page/Content/Modal/Components/PageModalTitle';
import { dispatchModalItemDeletedRefresh } from 'Hooks/useRefreshEvent';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import fr from 'facet-rest';
import { navigateToPageInNewTab } from 'Views/Page/Content/Templates';
import { ManagedAccountModelUpdateOpenCaseCard } from './ManagedAccountModelUpdateOpenCaseCard';


const useStyles = buildUseStyles('UpdateRequestLayout', (theme) => ({
  scrollable: {
    maxHeight: '100%',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  statusContainer: {
    marginRight: theme.spacing(3), // Move this away from the close button a bit
  },
}));

const Layout = () => {
  const classes = useStyles();
  const { onSubmit, resource } = useMetaLayoutContext();
  const {
    clearModalCollection,
  } = useModalContext();
  const isCancelled = resource.isTruthy('isCancelled');
  const isAssignedToOps = resource.attr('assignedToRole') === 'Owned by investment ops';
  const status = (resource.isTruthy('isComplete')) ? (
    <FDAlert title={'Completed'} status={'success'} />
  ) : (
    <FDAlert title={'Incomplete'} status={'warning'} />
  );
  return (
    <>
      <PageModalTitle padding={'none'}>
        <FDStack alignItems={'center'} spacing={4} justifyContent={'space-between'}>
          <FDStack alignItems={'center'} spacing={4} justifyContent="flex-start">
            <FDHeadlineText value={`Managed Account Model Update Request (${resource.id()})`} />
            <MetaField attr={'createdAt'} />
            <MetaField attr={'lastUpdated'} />
            {resource.meta().action('DELETE') && <FDDeleteButton
              label={'Cancel'}
              onClick={async () => {
                await resource.delete();
                dispatchModalItemDeletedRefresh();
                clearModalCollection();
              }}
            />}
          </FDStack>
          <div className={classes.statusContainer}>
            <FDStack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'flex-end'} wrap>
              {!isCancelled && <FDAlert title={resource.attr('assignedToRole')} status={'info'} />}
              {(isCancelled) ? (
                <>
                  <FDAlert title={'Cancelled'} status={'error'} />
                  <MetaField attr={'cancelledBy'} />
                </>
              ) : status}
            </FDStack>
          </div>
        </FDStack>
      </PageModalTitle>
      {
        resource.attr('pendingDisclaimers') && (
        <FDStack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'flex-start'} wrap>
          <FDAlert title={resource.attr('pendingDisclaimers')} status={'warning'} />
        </FDStack>
        )
      }
      {isAssignedToOps && (
      <FDStack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'flex-start'} wrap>
        <MetaField attr={'action'} />
        <MetaField attr={'assignedToAgentId'} flex={2} />
        <div className={classes.submitButton}>
          {resource.meta().action('PUT') && <FDPrimaryActionButton
            label={'Submit'}
            onClick={async () => {
              await onSubmit();
            }}
          />}
        </div>
      </FDStack>
      )}
      <div className={classes.scrollable}>
        <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
          <ManagedAccountModelUpdateOpenCaseCard />
          <FDPaper width={1} padding={400}>
            <FDStack direction={'column'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
              <FDStack direction={'row'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                <FDTitleText value={'Account Details'} />
              </FDStack>
              <FDStack direction={'row'} spacing={4} wrap alignItems="flex-start" justifyContent="flex-start">
                <MetaField attr={'householdId'} />
                <MetaField attr={'householdCapitalGainsBudget'} />
                <MetaField attr={'accountType'} />
                <MetaField attr={'accountNumber'} />
                <MetaField attr={'balance'} />
                <MetaField attr={'accountOwnerName'} />
                <MetaField attr={'leadPlannerName'} />
              </FDStack>
              <FDStack direction={'row'} spacing={1} alignItems="flex-start" justifyContent="flex-start" />
            </FDStack>
          </FDPaper>
          <FDPaper width={1} padding={400}>
            <FDStack direction={'column'} spacing={3} alignItems="flex-start" justifyContent="flex-start">
              <FDStack direction={'row'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                <FDTitleText value={'Model Details'} />
              </FDStack>
              { resource.attr('objective') && (
                <FDStack direction={'column'} spacing={2} alignItems="flex-start" justifyContent="flex-start">
                  <FDStack direction={'row'} spacing={2} alignItems={'center'} justifyContent="flex-start">
                    <FDBox width={250}>
                      <MetaField attr={'riskTolerance'} />
                    </FDBox>
                    <MetaField attr={'lastRiskToleranceChange'} />
                  </FDStack>
                  <FDStack direction={'row'} spacing={2} alignItems={'center'} justifyContent="flex-start">
                    <FDBox width={250}>
                      <MetaField attr={'objective'} />
                    </FDBox>
                    <MetaField attr={'lastObjectiveChange'} />
                  </FDStack>
                  <FDStack direction={'row'} spacing={2} alignItems={'center'} justifyContent="flex-start">
                    <FDBox width={250}>
                      <MetaField attr={'timeHorizon'} />
                    </FDBox>
                    <MetaField attr={'lastTimeHorizonChange'} />
                  </FDStack>
                </FDStack>
              )
              }
              <FDStack direction={'row'} spacing={4} alignItems="flex-start" justifyContent="flex-start">
                <MetaField attr={'currentModel'} />
                <MetaField attr={'suggestedModel'} />
              </FDStack>
            </FDStack>
          </FDPaper>
          <FDPaper width={1} padding={400}>
            <FDStack direction={'column'} spacing={1} alignItems="stretch" justifyContent="flex-start">
              <FDStack direction={'row'} spacing={1} alignItems="flex-start" justifyContent="flex-start">
                <FDTitleText value={'Model Update Details'} />
              </FDStack>
              <FDStack direction={'row'} spacing={4} alignItems="flex-start" justifyContent="flex-start">
                <MetaField attr={'selectedModel'} />
                <MetaField attr={'type'} />
                <MetaField attr={'muniModel'} />
                <MetaField attr={'isEsg'} />
                <MetaField attr={'isRetirement'} />
                <MetaField attr={'useDirectIndexing'} />
                <MetaField attr={'useAlternativeIncome'} />
              </FDStack>
              <FDStack direction={'column'} spacing={2} alignItems="stretch" justifyContent="flex-start">
                <MetaField attr={'modelAttribute'} />
                <MetaField attr={'deviationJustification'} />
                <MetaField attr={'deviationJustificationAdditionalDetails'} />
                <MetaField attr={'associatedRothIra'} />
                <MetaField attr={'alternativeIncomeAllocationPercentage'} />
                <MetaField attr={'alternativeIncomeDeviationJustification'} />
                <MetaField attr={'manualAlternativeIncomeJustification'} />
                <MetaField attr={'maxGains'} />
                <MetaField attr={'tradeTimeline'} />
                <MetaField attr={'expectedBalance'} />
                <MetaField attr={'esgPersona'} />
                <MetaField attr={'doNotBuyList'} />
              </FDStack>
              <MetaField attr={'memberHasApprovedThisChange'} />
              <FDStack direction={'row'} spacing={4} alignItems="flex-start" justifyContent="flex-start">
                <MetaFormFields resource={resource} />
              </FDStack>
            </FDStack>
          </FDPaper>
          {
            resource.withRelationship(fr.relationships.managedAccountTransactions.name, () => {
              return (
                <FDPaper width={1} padding={400}>
                  <FDTextButton label={'View transactions for this account'} onClick={() => navigateToPageInNewTab(resource.relationship(fr.relationships.managedAccountTransactions.name).link('related').url(), true)} />
                </FDPaper>
              );
            }, () => {})
          }
        </FDStack>
        {!isAssignedToOps && (
        <FDStack direction={'row'} alignItems={'center'} spacing={2} paddingY={2} justifyContent={'flex-start'} wrap>
          <MetaField attr={'action'} flex={2} />
          <MetaField attr={'assignedToAgentId'} />
        </FDStack>)}
      </div>
      {!isAssignedToOps && (
      <FDStack direction={'row'} alignItems={'center'} spacing={1} justifyContent={'flex-start'} wrap>
        <FDStack direction={'row'} width={'100%'} justifyContent={'center'} alignItems={'center'} spacing={2} paddingY={1}>
          {resource.meta().action('PUT') && <FDPrimaryActionButton
            label={'Submit'}
            onClick={async () => {
              await onSubmit();
            }}
          />}
          <MetaField attr={'assignToInvestmentOps'} />
        </FDStack>
      </FDStack>)}
    </>
  );
};

Layout.defaultProps = {};

Layout.propTypes = {};

export default Layout;
